import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { passwordGeneratorStore } from '../stores/password-generator-store';
import { PasswordDisplayCard } from '../components/password-display-card/password-display-card';
import { ConfigurationModal } from '../components/configuration-modal/configuration-modal';
import './password-generator-ui.scss';

export const PasswordGeneratorUI: React.FC = observer(() => {
  const store = passwordGeneratorStore;

  // Show configuration modal on startup
  useEffect(() => {
    store.openConfigModal();
  }, []);

  const handleGenerate = async () => {
    try {
      await store.generatePasswords();
    } catch (error) {
      console.error('Failed to generate passwords:', error);
      alert('Failed to generate passwords. Please check your configuration.');
    }
  };

  const handleCopy = async (password: string): Promise<boolean> => {
    try {
      return await store.copyPasswordToClipboard(password);
    } catch (error) {
      console.error('Failed to copy password:', error);
      return false;
    }
  };

  return (
    <div className="password-generator-ui">
      <div className="password-generator-ui__header">
        <h1 className="password-generator-ui__title">🔐 Password Generator</h1>
        <p className="password-generator-ui__subtitle">
          Generate secure passwords with customizable settings
        </p>
        <div className="password-generator-ui__header-actions">
          <button
            onClick={() => store.openConfigModal()}
            className="password-generator-ui__config-btn"
          >
            ⚙️ Configuration
          </button>
          <button
            onClick={handleGenerate}
            disabled={store.isGenerating || !store.isConfigValid}
            className="password-generator-ui__generate-btn"
          >
            {store.isGenerating ? '⏳ Generating...' : '🔐 Generate Passwords'}
          </button>
        </div>
      </div>

      <div className="password-generator-ui__content">
        {store.generatedPasswords.length > 0 && (
          <div className="password-generator-ui__results">
            <div className="password-generator-ui__results-header">
              <h2 className="password-generator-ui__results-title">
                Generated Passwords ({store.generatedPasswords.length})
              </h2>
              <div className="password-generator-ui__results-actions">
                <button
                  onClick={() => store.togglePasswordVisibility()}
                  className="password-generator-ui__toggle-btn"
                >
                  {store.showPasswords ? '🙈 Hide' : '👁️ Show'}
                </button>
                <button
                  onClick={() => store.clearAllPasswords()}
                  className="password-generator-ui__clear-btn"
                >
                  🗑️ Clear All
                </button>
              </div>
            </div>

            <div className="password-generator-ui__passwords-grid">
              {store.generatedPasswords.map((password) => (
                <PasswordDisplayCard
                  key={password.id}
                  password={password}
                  showPassword={store.showPasswords}
                  onCopy={handleCopy}
                  onRegenerate={(id) => store.regeneratePassword(id)}
                  onRemove={(id) => store.removePassword(id)}
                />
              ))}
            </div>

            {store.generatedPasswords.length > 1 && (
              <div className="password-generator-ui__stats">
                <h3>Strength Distribution</h3>
                <div className="password-generator-ui__stats-grid">
                  {Object.entries(store.strengthDistribution).map(([strength, count]) => (
                    <div key={strength} className="password-generator-ui__stat">
                      <span className="password-generator-ui__stat-label">
                        {strength.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                      </span>
                      <span className="password-generator-ui__stat-value">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {store.generatedPasswords.length === 0 && !store.isGenerating && (
          <div className="password-generator-ui__empty">
            <div className="password-generator-ui__empty-icon">🔐</div>
            <h3 className="password-generator-ui__empty-title">Ready to Generate</h3>
            <p className="password-generator-ui__empty-description">
              Click "Configuration" to set up your password preferences, then generate secure passwords.
            </p>
          </div>
        )}
      </div>

      <ConfigurationModal
        config={store.config}
        presets={store.allPresets}
        passwordCount={store.passwordCount}
        useMemorable={store.useMemorable}
        isOpen={store.showConfigModal}
        onConfigUpdate={(updates) => store.updateConfig(updates)}
        onPasswordCountChange={(count) => store.setPasswordCount(count)}
        onPresetApply={(preset) => store.applyPreset(preset)}
        onMemorableChange={(memorable) => store.setMemorable(memorable)}
        onGenerate={handleGenerate}
        onClose={() => store.closeConfigModal()}
      />
    </div>
  );
});
